import React, { useState } from 'react';
import { useBackupList } from './hooks/useBackupList';
import { ActionMenu } from '@/components/ui/ActionMenu';
import { Card, Badge } from '@/components/ui';
import { AuthButton } from '@/core/permissions/AuthButton';
import { Sparkles, Loader2, Database, ShieldCheck, DownloadCloud, HardDrive, ShieldAlert, History } from 'lucide-react';
import { cn } from '@/utils/cn';
import { ConfirmDialog } from '@/components/ui/ConfirmDialog';
import { toast } from 'sonner';

export function BackupListPage() {
  const { backups, isLoading, isCreating, createBackup, deleteBackup, isDeleting } = useBackupList();
  const [deleteId, setDeleteId] = useState<string | null>(null);

  const handleRestore = () => {
    toast.error('Tính năng Khôi phục (Restore) yêu cầu quyền Master Admin và xác thực OTP 2 lớp.');
  };

  return (
    <div className="space-y-6 animate-in fade-in slide-in-from-bottom-4 duration-500">
      {/* Header Info */}
      <div className="rounded-md border border-slate-100 bg-white p-3 shadow-sm flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
        <div className="flex items-center gap-3">
          <div className="space-y-1">
            <h1 className="text-sm font-medium text-slate-900">Sao lưu & Khôi phục</h1>
            <p className="text-xs text-slate-500">Quản lý các bản sao lưu an toàn (Snapshot) của toàn bộ dữ liệu dự án</p>
          </div>
        </div>
        <div className="flex flex-row items-center gap-2 overflow-x-auto">
          <AuthButton
            module="settings"
            action="update"
            behavior="disable"
            permissionReason="Bạn không có quyền tạo bản sao lưu"
            onClick={() => createBackup()}
            disabled={isCreating}
            className="flex h-10 items-center justify-center gap-2 rounded-md bg-emerald-700 px-4 text-xs font-medium text-white transition-all hover:bg-emerald-800 active:scale-95 disabled:opacity-50 shrink-0 shadow-md shadow-emerald-700/10"
          >
            {isCreating ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <Sparkles className="w-4 h-4" />
            )}
            Tạo bản sao lưu ngay
          </AuthButton>
        </div>
      </div>

      {/* Main List */}
      <Card className="rounded-3xl border-slate-100 bg-white shadow-sm overflow-hidden">
        <div className="overflow-x-auto custom-scrollbar">
          <table className="w-full text-left min-w-[800px]">
            <thead>
              <tr className="border-b border-slate-50 bg-slate-50/80 text-[11px] font-medium uppercase tracking-widest text-slate-400">
                <th className="py-4 pl-6">Tên bản sao lưu (Snapshot)</th>
                <th className="py-4">Dung lượng</th>
                <th className="py-4">Loại</th>
                <th className="py-4">Ngày tạo</th>
                <th className="py-4">Trạng thái</th>
                <th className="py-4 pr-6 text-right">Thao tác</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-50 text-sm">
              {isLoading && backups.length === 0 ? (
                Array.from({ length: 3 }).map((_, i) => (
                  <tr key={i} className="animate-pulse">
                    <td colSpan={6} className="p-4"><div className="h-12 rounded-xl bg-slate-50" /></td>
                  </tr>
                ))
              ) : backups.length === 0 ? (
                <tr>
                  <td colSpan={6} className="py-12 text-center text-slate-500">
                    <div className="flex flex-col items-center gap-3">
                      <div className="w-16 h-16 rounded-full bg-slate-50 flex items-center justify-center">
                        <Database className="w-8 h-8 text-slate-300" />
                      </div>
                      <p className="font-medium text-sm">Chưa có bản sao lưu nào được tạo.</p>
                    </div>
                  </td>
                </tr>
              ) : (
                backups.map((item) => (
                  <tr key={item.id} className="hover:bg-slate-50/50 transition-colors group">
                    <td className="py-4 pl-6">
                      <div className="flex items-center gap-3">
                        <div className={cn(
                          "w-8 h-8 rounded-lg flex items-center justify-center",
                          item.type === 'auto' ? "bg-blue-50 text-blue-600" : "bg-emerald-50 text-emerald-600"
                        )}>
                          <Database size={16} />
                        </div>
                        <span className="font-medium text-slate-700 group-hover:text-emerald-700 transition-colors">{item.name}</span>
                      </div>
                    </td>
                    <td className="py-4 text-slate-500 font-mono text-xs font-medium">{item.size}</td>
                    <td className="py-4">
                      <Badge className={cn(
                        "px-2.5 py-0.5 text-[10px] font-medium uppercase rounded-full",
                        item.type === 'auto' ? "bg-blue-100 text-blue-700" : "bg-slate-100 text-slate-600"
                      )}>
                        {item.type === 'auto' ? 'Tự động' : 'Thủ công'}
                      </Badge>
                    </td>
                    <td className="py-4 text-slate-500 font-medium text-xs">
                      {new Date(item.createdAt).toLocaleString('vi-VN')}
                    </td>
                    <td className="py-4">
                      <div className="flex items-center gap-2">
                        <div className="relative flex h-2 w-2">
                          <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75"></span>
                          <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-500"></span>
                        </div>
                        <span className="text-[11px] font-medium uppercase text-emerald-700">Hoàn tất</span>
                      </div>
                    </td>
                    <td className="py-4 pr-6 text-right">
                      <ActionMenu
                        options={[
                          { label: 'Tải xuống (.zip)', icon: 'ri-download-cloud-2-line', onClick: () => toast.success('Đang chuẩn bị file tải xuống...') },
                          { label: 'Khôi phục (Restore)', icon: 'ri-history-line', onClick: handleRestore },
                          { label: 'Xóa bản ghi', icon: 'ri-delete-bin-line', variant: 'danger', onClick: () => setDeleteId(item.id) },
                        ]}
                      />
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </Card>
      
      <ConfirmDialog 
        isOpen={!!deleteId}
        onClose={() => setDeleteId(null)}
        onConfirm={() => {
          if (deleteId) deleteBackup(deleteId);
          setDeleteId(null);
        }}
        title="Xóa bản sao lưu"
        message="Bạn có chắc chắn muốn xóa bản sao lưu này? Hành động này không thể hoàn tác và dữ liệu bên trong sẽ bị mất vĩnh viễn."
        confirmText="Xóa vĩnh viễn"
        isDestructive
        isLoading={isDeleting}
      />
    </div>
  );
}
